import { Action, Alert } from '@op/opds.opux';
import { space } from '@op/opds.tokens';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from './../../../i18n';
import { CoverageProduct } from '../../../dto/marketableProduct';
import { useAppStore } from '../../../state/store';
import {
  birthdateToAge,
  getInitialState,
  getSmallestMaxGrantingAge,
} from '../../../utils';
import { WorkingStatus } from '../../types/backgroundInfoForm';
import { type PurchasePathState } from '../../types/initialState';

const Notification = ({
  text,
  showLink = false,
  ...rest
}: {
  text: string;
  showLink?: boolean;
}) => (
  <Alert embedded {...rest}>
    <p style={{ marginBottom: space.small }}>{text}</p>
    {showLink && (
      <Action
        external
        href={i18n.t(
          'link.onlineBuyerHelp.ONLINE_BUYER_HELP_CUSTOMER_SERVICE_URL'
        )}>
        {i18n.t('link.onlineBuyerHelp.text')}
      </Action>
    )}
  </Alert>
);

const Notifications = () => {
  const { t } = useTranslation(['individualLife']);
  const { calculatorInput, marketableProduct, partyInformation } =
    getInitialState() as PurchasePathState;
  const coveragePremiums = useAppStore.use.coveragePremiums();
  const { dateOfBirth, workingStatus } = useAppStore.use.backgroundInfoForm();

  const lifeMaxAge = getSmallestMaxGrantingAge(
    marketableProduct.coverages.filter(
      (coverage) => coverage.coverageProduct === CoverageProduct.LIFE
    )
  );
  const criticalIllnessMaxAge = getSmallestMaxGrantingAge(
    marketableProduct.coverages.filter(
      (coverage) =>
        coverage.coverageProduct === CoverageProduct.CRITICAL_ILLNESS
    )
  );
  const disabilityMaxAge = getSmallestMaxGrantingAge(
    marketableProduct.coverages.filter((coverage) =>
      [
        CoverageProduct.TEMPORARY_DISABILITY,
        CoverageProduct.PERMANENT_DISABILITY,
      ].includes(coverage.coverageProduct)
    )
  );

  if (!lifeMaxAge || !criticalIllnessMaxAge || !disabilityMaxAge) {
    throw new Error(
      'One or more coverage max ages are not defined. Please check the marketable product data.'
    );
  }

  const age = birthdateToAge(dateOfBirth);
  let ageRestrictionText: string | undefined;

  if (age > criticalIllnessMaxAge && age <= lifeMaxAge) {
    ageRestrictionText = t(
      'coverageProduct.criticalIllnessAndDisabilityAgeNotification',
      {
        min: disabilityMaxAge,
        max: lifeMaxAge,
      }
    );
  } else if (age > disabilityMaxAge && age <= lifeMaxAge) {
    ageRestrictionText = t('coverageProduct.disabilityAgeNotification', {
      min: disabilityMaxAge,
      max: lifeMaxAge,
    });
  }

  const showDisabilityNotification = useCallback(
    () =>
      workingStatus === WorkingStatus.NO &&
      marketableProduct.coverages
        .filter((coverage) =>
          [
            CoverageProduct.TEMPORARY_DISABILITY,
            CoverageProduct.PERMANENT_DISABILITY,
          ].includes(coverage.coverageProduct)
        )
        .some((coverage) => coverage.preSelected) &&
      ageRestrictionText === undefined,
    [workingStatus, marketableProduct.coverages]
  );

  const showSuggestedSumExceedsMaxNotification = useCallback(
    () =>
      coveragePremiums?.some((premium) =>
        premium.premiums.some(
          (p) =>
            p.suggestedSumInsured.isSuggested &&
            p.suggestedSumInsured.exceedsMax
        )
      ),
    [coveragePremiums]
  );

  const shouldRenderDatesOfBirthDifferAlert = useCallback(() => {
    return (
      (calculatorInput &&
        calculatorInput.backgroundInfo?.dateOfBirth !==
          partyInformation?.dateOfBirth) ??
      ''
    );
  }, [dateOfBirth]);

  return (
    <>
      {ageRestrictionText && (
        <Notification
          text={ageRestrictionText}
          data-testid='age-notification'
        />
      )}
      {showDisabilityNotification() && (
        <Notification
          text={t('coverageProduct.disabilityNotification')}
          showLink
          data-testid='disability-notification'
        />
      )}
      {showSuggestedSumExceedsMaxNotification() && (
        <Notification
          text={t('coverageProduct.suggestedAmountExceedsMaxNotification')}
          showLink
          data-testid='suggested-sum-alert'
        />
      )}
      {shouldRenderDatesOfBirthDifferAlert() && (
        <Notification
          text={t('sumInsuredSelect.datesOfBirthDifferAlert')}
          data-testid='dates-of-birth-differ-alert'
        />
      )}
    </>
  );
};

export default Notifications;
